import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changePassword, validateChangePasswordInput } from '@/lib/changePassword';

interface ChangePasswordModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onSignInAgain?: () => void;
}

export default function ChangePasswordModal({
  open,
  email,
  onClose,
  onSignInAgain,
}: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setError(null);
    setSaving(false);
    setSuccess(false);
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const validationError = validateChangePasswordInput({
      currentPassword,
      newPassword,
      confirmPassword,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    const result = await changePassword({
      email,
      currentPassword,
      newPassword,
    });
    setSaving(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    setSuccess(true);
  }

  function handleClose() {
    if (saving) return;
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[11000] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 id="change-password-title" className="text-[#1A1A1A] text-base font-bold">
            {t('password.changeTitle')}
          </h3>
          <button
            type="button"
            disabled={saving}
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 cursor-pointer disabled:opacity-50"
          >
            <i className="ri-close-line text-[#374151]" />
          </button>
        </div>

        {success ? (
          <div className="px-5 py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-[#059669] text-xl" />
            </div>
            <p className="text-[#1A1A1A] text-sm font-semibold mb-2">{t('password.changeSuccess')}</p>
            <p className="text-[#9CA3AF] text-xs leading-relaxed">{t('password.changeSuccessHint')}</p>
            <div className="flex flex-col gap-2 mt-5">
              {onSignInAgain && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onSignInAgain();
                  }}
                  className="w-full py-3 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer"
                >
                  {t('password.signInAgain')}
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                className="w-full py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer"
              >
                {t('password.continue')}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} className="px-5 py-4 space-y-3">
            <PasswordField
              id="current-password"
              label={t('password.current')}
              value={currentPassword}
              onChange={setCurrentPassword}
              show={showCurrent}
              onToggleShow={() => setShowCurrent((v) => !v)}
              disabled={saving}
              autoComplete="current-password"
            />
            <PasswordField
              id="new-password"
              label={t('password.new')}
              value={newPassword}
              onChange={setNewPassword}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
              disabled={saving}
              autoComplete="new-password"
            />
            <PasswordField
              id="confirm-password"
              label={t('password.confirmNew')}
              value={confirmPassword}
              onChange={setConfirmPassword}
              show={showConfirm}
              onToggleShow={() => setShowConfirm((v) => !v)}
              disabled={saving}
              autoComplete="new-password"
            />

            {error && (
              <div className="flex items-start gap-2 bg-[#FEF2F2] rounded-xl px-3 py-2.5">
                <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5" />
                <p className="text-[#EF4444] text-xs">{error}</p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                disabled={saving}
                onClick={handleClose}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer disabled:opacity-50"
              >
                {t('password.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <i className="ri-loader-4-line animate-spin" />}
                {t('password.save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  show,
  onToggleShow,
  disabled,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggleShow: () => void;
  disabled?: boolean;
  autoComplete?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-[#555555] text-xs font-semibold mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoComplete={autoComplete}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pr-10 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] disabled:opacity-50"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={onToggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-[#9CA3AF] cursor-pointer"
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          <i className={show ? 'ri-eye-off-line' : 'ri-eye-line'} />
        </button>
      </div>
    </div>
  );
}
